import { useGetInitiativesQuery, InitiativeType } from "~/operations";
import { InitiativeOrderField, OrderDirection } from "~/operations";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useState } from "react";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { Header } from "~/types/table";

type SortField = InitiativeOrderField | "EFFORT" | "RISK_IMPROVEMENT";

export const useTopActionsCardWithInitiatives = (
  scope: SpaceOrWorkspaceScope,
) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");

  const [sortField, setSortField] = useState<SortField>("RISK_IMPROVEMENT");
  const [sortDirection, setSortDirection] = useState<OrderDirection>(
    OrderDirection.Desc,
  );
  const [page, setPage] = useState(1);

  const { data, loading, refetch } = useGetInitiativesQuery({
    variables: {
      first: 30,
      input: {
        entityMrn: scope.mrn,
        type: InitiativeType.Aggregate,
      },
    },
    skip: !scope || !isMoveTheNeedleFeatureEnabled,
    fetchPolicy: "network-only",
  });

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  const handleSortChange = (field: SortField, direction: OrderDirection) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
  };

  const initiativesEdges = data?.initiatives?.edges || [];
  const totalFindings = initiativesEdges.length;
  const initiativesNodes = initiativesEdges.flatMap((edge) => edge.node ?? []);

  const initiatives = initiativesNodes
    .sort((a, b) => {
      const direction = sortDirection === OrderDirection.Asc ? 1 : -1;
      const sortByUpdatedAt = () => a.updatedAt.localeCompare(b.updatedAt);
      const sortByScoreDelta = () => a.scoreDelta - b.scoreDelta;

      const position =
        (() => {
          switch (sortField) {
            case InitiativeOrderField.ScoreDelta:
              return sortByScoreDelta();
            case InitiativeOrderField.UpdatedAt:
              return sortByUpdatedAt();
            default:
              return sortByScoreDelta();
          }
        })() || sortByUpdatedAt();
      return position * direction;
    })
    .slice((page - 1) * 5, page * 5);

  const totalPages = Math.ceil(initiativesNodes.length / 5);

  const tableHeaders: Header<SortField>[] = [
    {
      id: "TOP_ACTIONS_FINDING",
      sortField: undefined,
      label: "Initiative",
      options: {
        textAlign: undefined,
        sx: {
          flex: 1,
        },
      },
    },
    {
      id: "ASSETS_COUNT",
      sortField: InitiativeOrderField.UpdatedAt,
      label: "Assets",
      options: undefined,
    },
    {
      id: "IMPACT",
      sortField: undefined,
      label: "Impact",
      options: undefined,
    },
    {
      id: "EFFORT",
      sortField: "EFFORT",
      label: "Mondoo Effort",
      options: undefined,
    },
    {
      id: "ACTION",
      sortField: undefined,
      label: "",
      options: {
        textAlign: "right",
        width: 159,
      },
    },
  ] as const;

  return {
    initiatives,
    totalFindings,
    loading,
    refetch,
    tableHeaders,
    sortField,
    sortDirection,
    handleSortChange,
    page,
    handlePageChange,
    totalPages,
  };
};
